import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  signInAction,
  clearMessage,
  verifyTwoFactorAction,
  cancelTwoFactorAction,
} from "../redux/actions/authActions";
import { RxCross1 } from "react-icons/rx";
import ButtonLoadingSpinner from "../components/loader/ButtonLoadingSpinner";
import BrandLogo from "../components/shared/BrandLogo";
import Button from "../components/shared/Button";
import TextInput from "../components/shared/TextInput";

const SignIn = () => {
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setLoadingText("Signing in...");
    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);
    const timeout = setTimeout(() => {
      setLoadingText(
        "This is taking longer than usual. Please wait while backend services are getting started."
      );
    }, 5000);
    await dispatch(signInAction(formData, navigate));
    setLoading(false);
    clearTimeout(timeout);
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (verifying || code.length < 6) return;
    setVerifying(true);
    await dispatch(verifyTwoFactorAction(code, navigate));
    setVerifying(false);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    setCode(
      useBackupCode
        ? value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 12)
        : value.replace(/\D/g, "").slice(0, 6)
    );
  };

  const toggleCodeMode = () => {
    setUseBackupCode(!useBackupCode);
    setCode("");
  };

  const handleCancelTwoFactor = () => {
    setCode("");
    setUseBackupCode(false);
    dispatch(cancelTwoFactorAction());
  };

  const signInError = useSelector((state) => state.auth?.signInError);
  const successMessage = useSelector((state) => state.auth?.successMessage);
  const twoFactorChallenge = useSelector(
    (state) => state.auth?.twoFactorChallenge
  );
  const challengeToken = twoFactorChallenge?.token;

  // Start every code step clean: a stale code or mode must not survive a new challenge.
  useEffect(() => {
    setCode("");
    setUseBackupCode(false);
  }, [challengeToken]);

  const handleClearMessage = () => {
    dispatch(clearMessage());
  };

  if (twoFactorChallenge) {
    return (
      <section className="min-h-screen bg-signin-pattern">
        <div className="container mx-auto flex min-h-screen flex-col items-center justify-center px-6">
          <form
            className="w-full max-w-md flex flex-col gap-6 rounded-lg border border-hairline bg-canvas p-8 shadow-card"
            onSubmit={handleVerify}
          >
            <div className="mx-auto flex justify-center">
              <BrandLogo className="h-10 w-auto" />
            </div>
            <div className="flex flex-col gap-1">
              <h1 className="text-title-md text-ink">
                Two-factor authentication
              </h1>
              <p className="text-body-md text-muted">
                Signing in as {twoFactorChallenge.email}
              </p>
            </div>
            {signInError && (
              <div
                className="flex items-center justify-between rounded-md border border-signature-coral bg-canvas px-4 py-3 text-body-md text-signature-coral"
                role="alert"
              >
                <span className="block sm:inline">{signInError}</span>
                <button
                  type="button"
                  className="text-signature-coral"
                  onClick={handleClearMessage}
                >
                  <RxCross1 className="h-3 w-3" />
                </button>
              </div>
            )}
            <p className="text-body-md text-body">
              {useBackupCode
                ? "Enter one of your backup codes."
                : "Enter the 6-digit code from your authenticator app."}
            </p>

            {useBackupCode ? (
              <TextInput
                id="backup-code"
                name="backupCode"
                type="text"
                label="Backup code"
                value={code}
                onChange={handleCodeChange}
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                autoFocus
                className="font-mono tracking-widest"
              />
            ) : (
              <TextInput
                id="totp-code"
                name="code"
                type="text"
                label="Authentication code"
                value={code}
                onChange={handleCodeChange}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                className="font-mono tracking-widest"
                helperText="Codes change every 30 seconds. If a code is rejected, check that your phone's date and time are set automatically."
              />
            )}

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={verifying || code.length < 6}
            >
              {verifying ? (
                <ButtonLoadingSpinner loadingText="Verifying..." />
              ) : (
                "Verify"
              )}
            </Button>

            <div className="flex flex-col items-center gap-3">
              <Button variant="link" size="sm" onClick={toggleCodeMode}>
                {useBackupCode
                  ? "Use authenticator app instead"
                  : "Use a backup code instead"}
              </Button>
              <Button variant="link" size="sm" onClick={handleCancelTwoFactor}>
                Back to sign in
              </Button>
            </div>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-signin-pattern">
      <div className="container mx-auto flex min-h-screen flex-col items-center justify-center px-6">
        <form
          className="w-full max-w-md flex flex-col gap-6 rounded-lg border border-hairline bg-canvas p-8 shadow-card"
          onSubmit={handleSubmit}
        >
          <div className="mx-auto flex justify-center">
            <BrandLogo className="h-10 w-auto" />
          </div>
          {signInError && (
            <div
              className="flex items-center justify-between rounded-md border border-signature-coral bg-canvas px-4 py-3 text-body-md text-signature-coral"
              role="alert"
            >
              <span className="block sm:inline">{signInError}</span>
              <button
                type="button"
                className="text-signature-coral"
                onClick={handleClearMessage}
              >
                <RxCross1 className="h-3 w-3" />
              </button>
            </div>
          )}
          {successMessage && (
            <div
              className="flex items-center justify-between rounded-md border border-success bg-canvas px-4 py-3 text-body-md text-success"
              role="alert"
            >
              <span className="block sm:inline">{successMessage}</span>
              <button
                type="button"
                className="text-success"
                onClick={handleClearMessage}
              >
                <RxCross1 className="h-3 w-3" />
              </button>
            </div>
          )}

          <TextInput
            id="email"
            name="email"
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            autoComplete="off"
          />

          <TextInput
            id="password"
            name="password"
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            required
            autoComplete="off"
          />

          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? (
              <ButtonLoadingSpinner loadingText={loadingText} />
            ) : (
              "Sign in"
            )}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default SignIn;
